import type { Category } from './constants';
import { formatMoney } from './money';
import type { Item, Outfit } from './types';

/** Top to bottom, the way an outfit is worn and a flat lay is laid out. */
const ZONE: Record<Category, number> = {
  Jacken: 0,
  Oberteile: 1,
  'Kleider & Röcke': 2,
  Hosen: 3,
  Schuhe: 4,
  Accessoires: 5,
  Sonstiges: 6,
};

/** The outfit's pieces that still exist, ordered for the collage: jacket first, shoes last. */
export function outfitPieces<T extends Pick<Item, 'id' | 'category'>>(outfit: Pick<Outfit, 'items'>, items: T[]): T[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const added = outfit.items.map((id) => byId.get(id)).filter((i): i is T => !!i);
  // stable: pieces of the same zone keep the order they were added in
  return added
    .map((item, index) => ({ item, index }))
    .sort((a, b) => ZONE[a.item.category] - ZONE[b.item.category] || a.index - b.index)
    .map((x) => x.item);
}

export interface CollageLayout {
  /** CSS grid-template-areas, one area letter per slot (a = the big one). */
  areas: string;
  columns: string;
  rows: string;
  /** How many pieces are shown; the last slot counts the rest. */
  slots: number;
  /** CSS aspect-ratio of the whole collage; varied so a board of pins looks like Pinterest. */
  aspect: string;
}

const LAYOUTS: CollageLayout[] = [
  { areas: '"a"', columns: '1fr', rows: '1fr', slots: 1, aspect: '4 / 5' },
  { areas: '"a"', columns: '1fr', rows: '1fr', slots: 1, aspect: '4 / 5' },
  { areas: '"a b"', columns: '1fr 1fr', rows: '1fr', slots: 2, aspect: '1 / 1' },
  { areas: '"a b" "a c"', columns: '1.25fr 1fr', rows: '1fr 1fr', slots: 3, aspect: '4 / 5' },
  { areas: '"a b" "c d"', columns: '1fr 1fr', rows: '1fr 1fr', slots: 4, aspect: '1 / 1' },
  { areas: '"a b" "a c" "d e"', columns: '1.25fr 1fr', rows: '1fr 1fr 1fr', slots: 5, aspect: '3 / 4' },
  { areas: '"a a b" "a a c" "d e f"', columns: '1fr 1fr 1fr', rows: '1fr 1fr 1fr', slots: 6, aspect: '1 / 1' },
];

export function collageLayout(count: number): CollageLayout {
  return LAYOUTS[Math.min(count, LAYOUTS.length - 1)]!;
}

export const SLOT_NAMES = 'abcdef';

export interface OutfitStatus {
  count: number;
  bought: number;
  /** Pieces still to buy and what they cost together. */
  missing: number;
  missingCents: number;
  totalCents: number;
  complete: boolean;
}

export function outfitStatus(pieces: Pick<Item, 'bought' | 'price_cents'>[]): OutfitStatus {
  const open = pieces.filter((p) => !p.bought);
  return {
    count: pieces.length,
    bought: pieces.length - open.length,
    missing: open.length,
    missingCents: open.reduce((s, p) => s + (p.price_cents || 0), 0),
    totalCents: pieces.reduce((s, p) => s + (p.price_cents || 0), 0),
    complete: pieces.length > 0 && open.length === 0,
  };
}

/** One line for a pin: "5 Teile · 2 fehlen · 89,98 €". */
export function outfitMeta(s: OutfitStatus, currency?: string): string {
  if (!s.count) return 'Noch leer';
  const parts = [`${s.count} ${s.count === 1 ? 'Teil' : 'Teile'}`];
  if (s.complete) parts.push('komplett');
  else {
    parts.push(`${s.missing} ${s.missing === 1 ? 'fehlt' : 'fehlen'}`);
    if (s.missingCents) parts.push(formatMoney(s.missingCents, currency));
  }
  return parts.join(' · ');
}

/**
 * How the rest of the outfit fits the budget. `leftCents` is the budget
 * minus what was bought already (Summary.budget.leftCents).
 */
export function outfitBudgetLine(s: OutfitStatus, leftCents: number | null, currency?: string): string {
  if (s.complete) return 'Alles gekauft.';
  if (leftCents === null || !s.missingCents) return '';
  const fmt = (c: number) => formatMoney(c, currency);
  const after = leftCents - s.missingCents;
  return after >= 0
    ? `Passt ins Budget, danach bleiben ${fmt(after)}.`
    : `Dafür fehlen ${fmt(-after)} im Budget.`;
}
